// frontend/src/components/patient/PatientSummaryPanel.js
//
// PatientSummaryPanel — read-only patient-safe visit summary + comment tool.
//
// Props: { appointmentId, schema, userId, role }
//   - Patients see the AI document summary (read-only) + one card per recording
//     summary, AND can select text to leave comments the doctor sees. When the
//     summary is hidden they see a placeholder.
//   - Clinicians (role !== 'patient') additionally get an Edit / Regenerate /
//     Hide-Show control row.
//
// TASK 11: the doc-summary area is a read-only Quill surface with an inline
// comment tool cloned from the clinician document-comment tool in
// MedStudentClinicPage.js (select -> highlight -> floating input -> save ->
// sidebar). Comments are stored in `public.patient_summary_comments` via the
// usePatientSummaryComments hook (realtime), and — unlike the clinician tool —
// patients ARE allowed to add comments here (no patientReadOnly guard).
//
// TASK 12: clinicians (role !== 'patient') additionally get a Resolve button on
// each open comment. Resolving flips the comment's status to 'resolved' and
// clears its highlight from the summary surface (re-anchoring by highlight_text
// if the stored index drifted — mirrors handleResolveComment in
// MedStudentClinicPage.js). Patients never see a Resolve button.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  Heading,
  HStack,
  Spinner,
  Text,
  Textarea,
  VStack,
  Badge,
  useToast,
} from "@chakra-ui/react";
import { usePatientVisitSummary } from "../../hooks/patient/usePatientVisitSummary";
import { usePatientSummaryComments } from "../../hooks/patient/usePatientSummaryComments";
import { supabasePublic } from "../supabaseClient";

// Same palette the clinician document-comment tool uses. Yellow is the default.
const HIGHLIGHT_COLORS = ["#FEF08A", "#BFDBFE", "#BBF7D0", "#FBCFE8"];
const DEFAULT_HIGHLIGHT = "#FEF08A";

// The backend summarizes whatever document text exists, so a visit whose note the
// provider hasn't written yet comes back with doc_summary: '' (not an error, not
// hidden). "No summary text" left patients guessing whether the portal was broken
// — name the reason instead.
const DOC_NOT_GENERATED_MESSAGE = "Document not generated yet by provider";
// Shown when the doctor pressed Hide. Kept separate from the message above:
// "not written yet" and "written but withheld" are different facts, and telling
// a patient the wrong one is misleading.
const SUMMARY_WITHHELD_MESSAGE = "Your provider has not shared this visit summary.";

/**
 * Read-only Quill rendering of the patient summary with the inline comment tool
 * layered on top. Text is anchored by { index, length } and re-anchored by
 * `highlight_text` after the summary is regenerated (mirrors the clinician tool).
 */
function SummaryCommentSurface({
  docSummary,
  appointmentId,
  role,
  target = "document",
  comments: allComments = [],
  addComment,
  resolveComment,
}) {
  // Only the comments attached to THIS surface (document vs a recording).
  // Memoized because this array is an effect dependency below: a fresh filter()
  // on every render re-ran that effect, which calls setBubblePositions() with a
  // new array, which renders again — an endless passive-update loop ("Maximum
  // update depth exceeded") that pegged the CPU whenever a summary was shown.
  const comments = useMemo(
    () => allComments.filter((c) => (c.target || "document") === target),
    [allComments, target]
  );
  const isClinician = role !== "patient";

  const quillHostRef = useRef(null);
  const quillRef = useRef(null);
  const commentsRef = useRef([]);
  const pendingHighlightRef = useRef(null);

  const [quillReady, setQuillReady] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [highlightColor, setHighlightColor] = useState(DEFAULT_HIGHLIGHT);
  const [floatPosition, setFloatPosition] = useState(null);
  const [bubblePositions, setBubblePositions] = useState([]);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  // General comment: feedback not tied to a highlighted passage (no selection).
  const [isAddingGeneral, setIsAddingGeneral] = useState(false);
  const [generalText, setGeneralText] = useState("");

  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  // ── Create the read-only Quill editor once. ────────────────────────────────
  useEffect(() => {
    if (!quillHostRef.current || quillRef.current) return;
    const quill = new Quill(quillHostRef.current, {
      theme: "snow",
      readOnly: true,
      modules: { toolbar: false },
    });
    quill.enable(false);
    quillRef.current = quill;
    if (quill.container && quill.container.parentNode) {
      quill.container.parentNode.style.position = "relative";
    }
    if (docSummary) quill.setText(docSummary, "silent");
    setQuillReady(true);
    // docSummary intentionally read once here; refreshed by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Keep the editor text in sync with the (possibly regenerated) summary. ──
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const shown = quill.getText().replace(/\n$/, "");
    if (shown !== (docSummary || "")) {
      quill.setText(docSummary || "", "silent");
    }
  }, [docSummary, quillReady]);

  // ── Resolve the current user (for comment authorship). ─────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const client = supabasePublic();
        const {
          data: { session },
        } = await client.auth.getSession();
        const user = session && session.user;
        if (!user) return;
        let name = user.email;
        try {
          const { data } = await client
            .from("users")
            .select("name")
            .eq("id", user.id)
            .single();
          if (data && data.name) name = data.name;
        } catch (_) {
          /* name lookup is best-effort */
        }
        if (!cancelled) setCurrentUser({ id: user.id, email: user.email, name });
      } catch (_) {
        /* unauthenticated / config not loaded — comments just post anonymously */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Re-anchor + (re)highlight open comments and compute bubble positions. ──
  // The summary text carries no stored formatting (it is set from plain text on
  // every load/regeneration), so highlights are re-applied here each time.
  const updateBubblePositions = useCallback(() => {
    const quill = quillRef.current;
    if (!quill) {
      setBubblePositions([]);
      return;
    }
    const current = commentsRef.current || [];
    const positions = [];
    current.forEach((comment) => {
      if (comment.status !== "open" || !comment.selection_range) return;
      let { index, length } = comment.selection_range;
      try {
        if (comment.highlight_text) {
          const at = quill.getText(index, length);
          if (at !== comment.highlight_text) {
            // Text moved (regeneration/edit) — search for the highlighted text
            // and re-anchor to the closest occurrence.
            const full = quill.getText();
            let searchIdx = full.indexOf(comment.highlight_text);
            let best = -1;
            let bestDist = Infinity;
            while (searchIdx !== -1) {
              const d = Math.abs(searchIdx - index);
              if (d < bestDist) {
                bestDist = d;
                best = searchIdx;
              }
              searchIdx = full.indexOf(comment.highlight_text, searchIdx + 1);
            }
            if (best !== -1) {
              index = best;
              length = comment.highlight_text.length;
            } else {
              // No longer present — drop the highlight/bubble; it still shows in
              // the sidebar so the comment is never lost.
              return;
            }
          }
        }
        quill.formatText(
          index,
          length || 1,
          "background",
          comment.highlight_color || DEFAULT_HIGHLIGHT,
          "silent"
        );
        const bounds = quill.getBounds(index, length || 1);
        if (bounds) {
          const el = quill.container;
          const offTop = el.offsetTop || 0;
          const offLeft = el.offsetLeft || 0;
          const bubbleTop = offTop + bounds.top - 22;
          const bubbleLeft = Math.max(
            offLeft + 6,
            Math.min(
              offLeft + el.clientWidth - 28,
              offLeft + bounds.left + bounds.width - 10
            )
          );
          positions.push({ id: comment.id, comment, top: bubbleTop, left: bubbleLeft });
        }
      } catch (_) {
        /* ignore transient measuring errors */
      }
    });
    setBubblePositions(positions);
  }, []);

  // Recompute on comment changes, on edits, and on a slow interval (covers the
  // async load ordering of Quill vs. the comments fetch).
  useEffect(() => {
    if (!quillReady) return undefined;
    const quill = quillRef.current;
    updateBubblePositions();
    const interval = setInterval(updateBubblePositions, 1000);
    const onChange = () => updateBubblePositions();
    quill.on("text-change", onChange);
    quill.on("selection-change", onChange);
    return () => {
      clearInterval(interval);
      quill.off("text-change", onChange);
      quill.off("selection-change", onChange);
    };
  }, [quillReady, comments, updateBubblePositions]);

  // ── Selection → position the floating "add comment" affordance. ────────────
  useEffect(() => {
    if (!quillReady) return undefined;
    const quill = quillRef.current;
    const handleSelection = (range) => {
      if (range && range.length > 0) {
        setSelectedRange({ index: range.index, length: range.length });
        setSelectedText(quill.getText(range.index, range.length));
        try {
          const bounds = quill.getBounds(range.index, range.length);
          const el = quill.container;
          const offTop = el.offsetTop || 0;
          const offLeft = el.offsetLeft || 0;
          const width = 260;
          const left = Math.max(
            offLeft + 6,
            Math.min(
              offLeft + el.clientWidth - width - 6,
              offLeft + bounds.left + (bounds.width - width) / 2
            )
          );
          const top =
            offTop + bounds.top > 130
              ? offTop + bounds.top - 130
              : offTop + bounds.top + bounds.height + 8;
          setFloatPosition({ top, left });
        } catch (_) {
          /* ignore */
        }
      } else {
        // Click-away: close the composer unless focus moved into our own UI.
        setTimeout(() => {
          const active = document.activeElement;
          const inside =
            active &&
            active.closest &&
            (active.closest(".psc-comments-sidebar") ||
              active.closest(".psc-comment-float"));
          if (!inside) {
            setFloatPosition(null);
            setIsAddingComment(false);
          }
        }, 200);
      }
    };
    quill.on("selection-change", handleSelection);
    return () => quill.off("selection-change", handleSelection);
  }, [quillReady]);

  // Strip the temporary compose highlight if the composer closes without saving.
  useEffect(() => {
    const quill = quillRef.current;
    if (!isAddingComment && pendingHighlightRef.current && quill) {
      const { index, length } = pendingHighlightRef.current;
      quill.formatText(index, length, "background", false, "silent");
      pendingHighlightRef.current = null;
      updateBubblePositions();
    }
  }, [isAddingComment, updateBubblePositions]);

  const openComposer = () => {
    const quill = quillRef.current;
    if (!selectedRange || selectedRange.length <= 0 || !quill) return;
    quill.formatText(
      selectedRange.index,
      selectedRange.length,
      "background",
      highlightColor,
      "silent"
    );
    pendingHighlightRef.current = { ...selectedRange };
    setIsAddingComment(true);
  };

  const cancelComposer = () => {
    setIsAddingComment(false);
    setNewCommentText("");
    setHighlightColor(DEFAULT_HIGHLIGHT);
  };

  const saveComment = async () => {
    if (!newCommentText.trim() || !selectedRange) return;
    try {
      // NOTE: no patientReadOnly guard — patients may comment on their summary.
      await addComment({
        content: newCommentText,
        highlightText: selectedText,
        selectionRange: selectedRange,
        color: highlightColor,
        user: currentUser,
        target,
      });
      // Keep the highlight on the saved range; null the ref so the cleanup
      // effect doesn't strip it.
      pendingHighlightRef.current = null;
      const quill = quillRef.current;
      if (quill) {
        quill.formatText(
          selectedRange.index,
          selectedRange.length,
          "background",
          highlightColor,
          "silent"
        );
      }
      setIsAddingComment(false);
      setNewCommentText("");
      setHighlightColor(DEFAULT_HIGHLIGHT);
      updateBubblePositions();
    } catch (_) {
      /* surfaced by the realtime refetch; keep composer open on failure */
    }
  };

  // ── General comment: not anchored to any text (null selection/highlight). ────
  // Shows in the sidebar like the rest, but gets no highlight bubble on the doc.
  const saveGeneralComment = async () => {
    if (!generalText.trim()) return;
    try {
      await addComment({
        content: generalText,
        highlightText: null,
        selectionRange: null,
        color: null,
        user: currentUser,
        target,
      });
      setGeneralText("");
      setIsAddingGeneral(false);
    } catch (_) {
      /* realtime refetch reconciles if the insert didn't land */
    }
  };

  // ── Clinician resolve: drop the highlight, then flip status to resolved. ────
  // Mirrors handleResolveComment (MedStudentClinicPage.js:1177): re-anchor by
  // highlight_text when the stored index has drifted (regeneration/edit), then
  // clear the `background` format over that range. The summary surface re-paints
  // highlights for OPEN comments only, so we optimistically mark the comment
  // resolved in the ref to stop the interval re-painting it before the refetch.
  const handleResolve = useCallback(
    async (comment) => {
      if (!comment) return;
      const quill = quillRef.current;
      if (quill && comment.selection_range) {
        let targetIndex = comment.selection_range.index;
        let targetLength = comment.selection_range.length;
        if (comment.highlight_text) {
          const text = quill.getText();
          const sub = text.substring(targetIndex, targetIndex + targetLength);
          if (sub !== comment.highlight_text) {
            let foundIdx = text.indexOf(comment.highlight_text);
            while (foundIdx !== -1) {
              const format = quill.getFormat(foundIdx, 1);
              if (format && format.background) {
                targetIndex = foundIdx;
                targetLength = comment.highlight_text.length;
                break;
              }
              foundIdx = text.indexOf(comment.highlight_text, foundIdx + 1);
            }
          }
        }
        quill.formatText(targetIndex, targetLength || 1, "background", false, "silent");
      }
      commentsRef.current = (commentsRef.current || []).map((c) =>
        c.id === comment.id ? { ...c, status: "resolved" } : c
      );
      if (activeCommentId === comment.id) setActiveCommentId(null);
      updateBubblePositions();
      try {
        await resolveComment(comment.id);
      } catch (_) {
        /* realtime/refetch reconciles if the update didn't land */
      }
    },
    [activeCommentId, resolveComment, updateBubblePositions]
  );

  const openComments = (comments || []).filter((c) => c.status === "open");
  const showSidebar = openComments.length > 0 || isAddingComment;

  return (
    <Box display="flex" gap={4} alignItems="flex-start" flexWrap="wrap">
      <Box position="relative" flex="1 1 320px" minW={0}>
        <Box
          ref={quillHostRef}
          data-summary-doc="true"
          className="patient-summary-doc"
          bg="white"
          borderRadius="md"
          sx={{
            ".ql-container": { border: "1px solid", borderColor: "gray.200", borderRadius: "0.375rem", fontSize: "15px" },
            ".ql-editor": { minHeight: "80px", color: "gray.800" },
          }}
        />

        {/* Comment bubbles anchored on the highlighted text. */}
        {quillReady &&
          bubblePositions.map((pos) => {
            const isActive = activeCommentId === pos.id;
            const initial = (pos.comment.user_name || "A").substring(0, 1).toUpperCase();
            return (
              <button
                key={pos.id}
                onClick={() => setActiveCommentId(isActive ? null : pos.id)}
                title={pos.comment.content}
                style={{
                  position: "absolute",
                  top: `${pos.top}px`,
                  left: `${pos.left}px`,
                  zIndex: 10,
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: pos.comment.highlight_color || DEFAULT_HIGHLIGHT,
                  color: "#3f3f46",
                  fontSize: "10px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isActive
                    ? "0 0 0 2px #3182ce, 0 2px 5px rgba(0,0,0,0.2)"
                    : "0 2px 5px rgba(0,0,0,0.2)",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                {initial}
              </button>
            );
          })}

        {/* Floating "add comment" affordance for the current selection. */}
        {quillReady && floatPosition && selectedRange && !isAddingComment && (
          <button
            className="psc-comment-float"
            onMouseDown={(e) => e.preventDefault()}
            onClick={openComposer}
            style={{
              position: "absolute",
              top: `${floatPosition.top}px`,
              left: `${floatPosition.left}px`,
              zIndex: 100,
              background: "#2196f3",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "5px 10px",
              fontSize: "11px",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(33,150,243,0.35)",
            }}
          >
            + Add comment
          </button>
        )}

        {/* Comment composer. */}
        {quillReady && floatPosition && isAddingComment && (
          <div
            className="psc-comment-float"
            style={{
              position: "absolute",
              top: `${floatPosition.top}px`,
              left: `${floatPosition.left}px`,
              zIndex: 100,
              width: "260px",
              background: "#fff",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #2196f3",
              boxShadow: "0 4px 12px rgba(33,150,243,0.2)",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <p style={{ fontSize: "10px", color: "#6c757d", margin: 0 }}>
              New comment on: "
              {selectedText.length > 25
                ? `${selectedText.substring(0, 22)}...`
                : selectedText}
              "
            </p>
            <textarea
              placeholder="Add a comment..."
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              autoFocus
              style={{
                width: "100%",
                fontSize: "12px",
                padding: "6px",
                borderRadius: "4px",
                border: "1px solid #ced4da",
                minHeight: "50px",
                resize: "none",
                outline: "none",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "10px", color: "#6c757d" }}>Highlight:</span>
              <div style={{ display: "flex", gap: "4px" }}>
                {HIGHLIGHT_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setHighlightColor(color)}
                    title={color}
                    style={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      background: color,
                      border:
                        highlightColor === color
                          ? "2px solid #2196f3"
                          : "1px solid #ced4da",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
              <button
                onClick={cancelComposer}
                style={{
                  fontSize: "10px",
                  background: "none",
                  border: "none",
                  color: "#868e96",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveComment}
                style={{
                  fontSize: "10px",
                  background: "#2196f3",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Comment
              </button>
            </div>
          </div>
        )}

        {/* General comment — not tied to a text selection. Always available so
            the patient can leave feedback without highlighting a passage. */}
        <Box mt={3}>
          {!isAddingGeneral ? (
            <Button
              size="sm"
              variant="outline"
              color="inherit"
              borderColor="rgba(128,128,128,0.4)"
              _hover={{ bg: "rgba(128,128,128,0.12)" }}
              onClick={() => setIsAddingGeneral(true)}
            >
              💬 Leave a comment for your doctor
            </Button>
          ) : (
            <Box
              borderWidth="1px"
              borderColor="rgba(128,128,128,0.4)"
              borderRadius="md"
              p={3}
              bg="rgba(128,128,128,0.06)"
            >
              <Textarea
                value={generalText}
                onChange={(e) => setGeneralText(e.target.value)}
                placeholder="Write a comment for your doctor…"
                size="sm"
                rows={3}
                color="inherit"
                autoFocus
              />
              <HStack mt={2} justify="flex-end" spacing={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  color="inherit"
                  onClick={() => {
                    setIsAddingGeneral(false);
                    setGeneralText("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  colorScheme="blue"
                  onClick={saveGeneralComment}
                  isDisabled={!generalText.trim()}
                >
                  Comment
                </Button>
              </HStack>
            </Box>
          )}
        </Box>
      </Box>

      {/* Comments sidebar. */}
      {showSidebar && (
        <Box
          className="psc-comments-sidebar"
          flex="0 0 260px"
          maxW="260px"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          bg="gray.50"
          p={3}
          sx={{ ".dark &": { bg: "whiteAlpha.100", borderColor: "whiteAlpha.300" } }}
        >
          <Heading size="xs" mb={2} color="gray.600" sx={{ ".dark &": { color: "gray.100" } }}>
            Comments
          </Heading>
          {openComments.length === 0 ? (
            <Text fontSize="xs" color="gray.500">
              Select text in the summary to add a comment.
            </Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {openComments.map((comment) => (
                <Box
                  key={comment.id}
                  p={2}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={
                    comment.id === activeCommentId ? "blue.300" : "gray.200"
                  }
                  bg={comment.id === activeCommentId ? "blue.50" : "white"}
                  cursor="pointer"
                  onClick={() => setActiveCommentId(comment.id)}
                >
                  <Text fontSize="xs" fontWeight="600" color="gray.700">
                    {comment.user_name || "User"}
                  </Text>
                  <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap" mt={1}>
                    {comment.content}
                  </Text>
                  {comment.highlight_text && (
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      fontStyle="italic"
                      mt={1}
                      noOfLines={1}
                      title={comment.highlight_text}
                    >
                      "{comment.highlight_text}"
                    </Text>
                  )}
                  {isClinician && (
                    <Button
                      size="xs"
                      mt={2}
                      colorScheme="green"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResolve(comment);
                      }}
                    >
                      Resolve
                    </Button>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );
}

function PatientSummaryPanel({ appointmentId, schema, userId, role }) {
  const { summary, loading, error, regenerate, update } = usePatientVisitSummary(
    appointmentId,
    schema,
    userId
  );
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const isClinician = role !== "patient";

  // One comment store for the whole visit (document + every recording); each
  // surface below filters to its own `target`. A clinician opening this panel
  // marks all comments seen, which clears the doctor's notification badge.
  const { comments, addComment, resolveComment, markCommentsSeen } =
    usePatientSummaryComments(appointmentId, role);
  useEffect(() => {
    if (isClinician) markCommentsSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) {
        toast({ title: successMsg, status: "success", duration: 2000, isClosable: true });
      }
    } catch (e) {
      toast({
        title: (e && e.message) || "Action failed",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  const startEditing = () => {
    setDraft((summary && summary.doc_summary) || "");
    setIsEditing(true);
  };

  const handleSaveEdit = () =>
    runAction(async () => {
      await update({ doc_summary: draft });
      setIsEditing(false);
    }, "Summary saved");

  const handleRegenerate = () =>
    runAction(() => regenerate(), "Summary regenerated");

  const handleToggleHidden = () =>
    runAction(() => update({ hidden: !(summary && summary.hidden) }));

  if (loading) {
    return (
      <Center py={10}>
        <Spinner size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Box py={4}>
        <Text color="red.500">We couldn't load this visit summary.</Text>
      </Box>
    );
  }

  // Time gate: patients can't see the summary until after their appointment. The
  // backend returns this flag (and never generates the summary early).
  if (summary?.not_available_yet && role === "patient") {
    const when = summary.available_at ? new Date(summary.available_at) : null;
    const whenLabel = when && !isNaN(when.getTime()) ? when.toLocaleString() : null;
    return (
      <Box py={8} textAlign="center">
        <Heading size="sm" color="inherit" mb={2}>Summary not available yet</Heading>
        <Text color="inherit" opacity={0.85}>
          Your visit summary will be ready after your appointment
          {whenLabel ? ` on ${whenLabel}` : ""}. Check back once your visit is done.
        </Text>
      </Box>
    );
  }

  // No payload at all — nothing to show.
  if (!summary) {
    return (
      <Box py={4}>
        <Text color="gray.500">No summary is available for this visit yet.</Text>
      </Box>
    );
  }

  // The doctor pressed Hide. That withholds the AI-written summaries ONLY — the
  // backend deliberately keeps returning reports and forms for a hidden visit
  // (see patient_visit_summary: "the doctor's 'hide' only blanks the AI
  // summaries"). This used to blank the whole panel, so patients lost access to
  // their own uploaded files too. Distinct from "the note isn't written yet",
  // which the document section handles below.
  const summaryWithheld = Boolean(summary.hidden) && role === "patient";

  const audioSummaries = summaryWithheld ? [] : (summary.audio_summaries || []);
  const forms = summary.forms || [];
  // Trimmed: a summary of an empty document can come back as whitespace, which
  // would otherwise render an empty Quill surface instead of the explanation.
  const hasDocSummary = Boolean(String(summary.doc_summary || "").trim());

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" align="center">
        <Heading size="md" color="inherit" sx={{ ".dark &": { color: "white" } }}>Visit summary</Heading>
        {isClinician && summary.hidden && (
          <Badge colorScheme="orange">Hidden from patient</Badge>
        )}
      </HStack>

      {isClinician && (
        <HStack spacing={3}>
          <Button size="sm" onClick={startEditing} isDisabled={busy || isEditing}>
            Edit
          </Button>
          <Button size="sm" onClick={handleRegenerate} isLoading={busy}>
            Regenerate
          </Button>
          <Button size="sm" onClick={handleToggleHidden} isDisabled={busy}>
            {summary.hidden ? "Show" : "Hide"}
          </Button>
        </HStack>
      )}

      {isClinician && isEditing ? (
        <Box>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={10}
            placeholder="Patient-facing visit summary"
          />
          <HStack mt={2} spacing={3}>
            <Button size="sm" colorScheme="blue" onClick={handleSaveEdit} isLoading={busy}>
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsEditing(false)}
              isDisabled={busy}
            >
              Cancel
            </Button>
          </HStack>
        </Box>
      ) : summary.generation_failed ? (
        // The AI call failed (e.g. exhausted API credits) — the note may exist,
        // we just couldn't summarize it. Saying "not written yet" here would
        // wrongly blame the provider. Nothing was cached, so it retries on the
        // next load once the underlying problem is fixed.
        <Box
          data-summary-doc="true"
          className="patient-summary-doc"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          bg="whiteAlpha.50"
          sx={{ ".dark &": { borderColor: "whiteAlpha.300" } }}
        >
          <Text color="gray.600" sx={{ ".dark &": { color: "gray.200" } }}>
            {isClinician
              ? "The summary could not be generated — check the AI provider configuration."
              : "This summary isn't ready yet. Please check back shortly."}
          </Text>
        </Box>
      ) : summaryWithheld ? (
        // Withheld by the doctor. Say so plainly rather than reusing the
        // "not written yet" copy — the note may well exist; it is just not
        // being shared. Reports and forms still render below.
        <Box
          data-summary-doc="true"
          className="patient-summary-doc"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          bg="whiteAlpha.50"
          sx={{ ".dark &": { borderColor: "whiteAlpha.300" } }}
        >
          <Text color="gray.600" sx={{ ".dark &": { color: "gray.200" } }}>
            {SUMMARY_WITHHELD_MESSAGE}
          </Text>
          <Text fontSize="sm" mt={1} color="gray.500" sx={{ ".dark &": { color: "gray.400" } }}>
            Your forms from this visit are still shown below, and your
            reports are in the Reports tab of the sidebar.
          </Text>
        </Box>
      ) : hasDocSummary ? (
        // Read-only Quill summary with the inline comment tool (Task 11).
        <SummaryCommentSurface
          docSummary={summary.doc_summary}
          appointmentId={appointmentId}
          role={role}
          target="document"
          comments={comments}
          addComment={addComment}
          resolveComment={resolveComment}
        />
      ) : (
        // Scoped to the DOCUMENT section on purpose: recordings, reports and forms
        // are produced independently of the note, so a visit routinely has those
        // before the provider has written anything. Returning early here would
        // hide content the patient can already read, so the explanation sits in
        // the document's place and everything below still renders.
        <Box
          data-summary-doc="true"
          className="patient-summary-doc"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          bg="whiteAlpha.50"
          sx={{ ".dark &": { borderColor: "whiteAlpha.300" } }}
        >
          {/* Clinicians keep the neutral empty-state wording — they ARE the
              provider, and still get Edit/Regenerate above this. */}
          <Text color="gray.600" sx={{ ".dark &": { color: "gray.200" } }}>
            {isClinician ? "No visit summary text yet." : DOC_NOT_GENERATED_MESSAGE}
          </Text>
          {!isClinician && (
            <Text
              fontSize="sm"
              mt={1}
              color="gray.500"
              sx={{ ".dark &": { color: "gray.400" } }}
            >
              It will appear here once your provider has written it.
            </Text>
          )}
        </Box>
      )}

      {audioSummaries.length > 0 && (
        <VStack align="stretch" spacing={3}>
          <Heading size="sm" color="inherit" sx={{ ".dark &": { color: "white" } }}>Recording summaries</Heading>
          {audioSummaries.map((a, idx) => (
            <Card key={a.audio_number != null ? a.audio_number : idx} variant="outline">
              <CardHeader pb={1}>
                <Heading size="xs">
                  {a.label || `Audio ${a.audio_number != null ? a.audio_number : idx + 1}`}
                </Heading>
                {a.date && (
                  <Text fontSize="xs" color="gray.500">
                    {a.date}
                  </Text>
                )}
              </CardHeader>
              <CardBody pt={1}>
                {/* Each recording summary is itself commentable (highlight a
                    passage or leave a general comment), tagged to this audio. */}
                <SummaryCommentSurface
                  docSummary={a.summary}
                  appointmentId={appointmentId}
                  role={role}
                  target={`audio:${a.audio_number != null ? a.audio_number : idx + 1}`}
                  comments={comments}
                  addComment={addComment}
                  resolveComment={resolveComment}
                />
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}

      {/* "Reports & files" used to render here. Removed once the patient
          portal grew a right sidebar with a dedicated Reports tab — that tab
          lists the same files grouped by classification, with previews and an
          upload button, so an unstyled duplicate list at the foot of the
          summary was redundant. The clinician preview drops it too, so what
          they see still matches what the patient sees. */}
      {forms.length > 0 && (
        <VStack align="stretch" spacing={2}>
          <Heading size="sm" color="inherit" sx={{ ".dark &": { color: "white" } }}>Forms</Heading>
          {forms.map((f, i) => (
            <Box
              key={i}
              as="a"
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              display="flex"
              alignItems="center"
              gap={2}
              px={3}
              py={2}
              borderRadius="md"
              border="1px solid"
              borderColor="rgba(128,128,128,0.4)"
              bg="rgba(128,128,128,0.08)"
              color="inherit"
              _hover={{ bg: "rgba(128,128,128,0.18)", textDecoration: "none" }}
            >
              <span aria-hidden>📝</span>
              <Text fontSize="sm">{f.label || `Form ${i + 1}`}</Text>
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
}

export default PatientSummaryPanel;
